import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ABOUT_ROUTE, ADMISSION_ROUTE, CONTACT_ROUTE, FACULTY_ROUTE, HOME_ROUTE } from "../../routes/rootRoutes";
import AppLogo from "../../assets/images/logo.png";
import Nav from "./Nav";
import {
  FaBars,
  FaTimes,
  FaHome,
  FaInfoCircle,
  FaGraduationCap,
  FaUserPlus,
  FaBuilding,
  FaPhoneAlt,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { CONTACT, ESTABLISH_YEAR, LOGO_LABEL, LOGO_SLOGAN } from "../../utils/constant";

const Header = () => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeNav, setActiveNav] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isNavOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isNavOpen]);

  const navItems = [
    { path: "/", label: "Home", icon: <FaHome /> },
    { path: ABOUT_ROUTE, label: "About", icon: <FaInfoCircle /> },
    { path: ADMISSION_ROUTE, label: "Admissions", icon: <FaUserPlus /> },
    { path: FACULTY_ROUTE, label: "Facilities", icon: <FaBuilding /> },
    { path: CONTACT_ROUTE, label: "Contact", icon: <FaPhoneAlt /> },
  ];

  return (
    <header className="relative">
      <Nav />

      {/* Main Header with Sticky Nav */}
      <div
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-lg shadow-xl"
            : "bg-white shadow-lg"
        }`}
      >
        <div className="container px-4 mx-auto">
          <div className="flex items-center justify-between py-3 md:py-4">
            {/* Logo and Brand */}
            <div className="flex items-center space-x-3 md:space-x-4">
              <Link
                to={HOME_ROUTE}
                className="flex items-center space-x-3 md:space-x-4 group"
                onMouseEnter={() => setActiveNav("home")}
                onMouseLeave={() => setActiveNav("")}
              >
                <div className="relative">
                  <div className="absolute -inset-1.5 bg-blue-100 rounded-full blur opacity-0 group-hover:opacity-60 transition duration-300"></div>
                  <img
                    src={AppLogo}
                    className="relative w-auto h-10 transition duration-300 transform md:h-12 lg:h-14 group-hover:scale-105"
                    alt="logo"
                  />
                </div>
                <div className="block">
                  <h1 className="text-sm font-bold leading-tight tracking-tight text-gray-900 transition-colors duration-300 md:text-xl lg:text-2xl group-hover:text-blue-800">
                    {LOGO_LABEL}
                  </h1>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-blue-700 font-semibold text-[10px] md:text-sm tracking-wide group-hover:text-blue-600 transition-colors duration-300">
                      {LOGO_SLOGAN}
                    </p>
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  </div>
                  <p className="text-[10px] sm:text-xs text-gray-600 mt-0.5 font-medium group-hover:text-gray-800 transition-colors duration-300">
                    Excellence in Education Since {ESTABLISH_YEAR}
                  </p>
                </div>
              </Link>
            </div>
            <div className="items-center hidden space-x-6 lg:flex">
              <nav className="flex items-center space-x-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="relative px-4 py-2 font-semibold tracking-wide text-gray-700 transition-all duration-300 hover:text-blue-700 group"
                    onMouseEnter={() => setActiveNav(item.label.toLowerCase())}
                    onMouseLeave={() => setActiveNav("")}
                  >
                    <span className="relative z-10 text-[15px] transition-all duration-300 group-hover:translate-y-[-2px] group-hover:scale-[1.05]">
                      {item.label}
                    </span>

                    {/* Soft Glow Underline */}
                    <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-blue-600 transform scale-x-0 group-hover:scale-x-100 transition-all duration-300 origin-center"></div>

                    {/* Subtle Glow Blur Layer */}
                    <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-blue-400 blur-sm opacity-0 group-hover:opacity-100 transform scale-x-0 group-hover:scale-x-100 transition-all duration-300 origin-center"></div>

                    {/* Active Indicator Using Framer Motion */}
                    {activeNav === item.label.toLowerCase() && (
                      <motion.div
                        layoutId="nav-active-line"
                        className="absolute bottom-0 left-4 right-4 h-[2px] bg-blue-700"
                        transition={{ duration: 0.25 }}
                      />
                    )}
                  </Link>
                ))}
              </nav>

              {/* CTA Button */}
              <div className="flex items-center ml-4 space-x-4">
                <button className="relative group">
                  <div className="absolute inset-0 transition duration-500 opacity-0 bg-gradient-to-r from-blue-600 to-green-500 rounded-xl blur-lg group-hover:opacity-40"></div>

                  <div className="relative py-3 text-sm font-semibold text-white transition-all duration-300 bg-gradient-to-r from-blue-700 to-blue-600 px-7 rounded-xl group-hover:-translate-y-1 group-hover:shadow-xl">
                    Apply Now
                  </div>
                </button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsNavOpen(!isNavOpen)}
              className="relative lg:hidden group"
              aria-label="Toggle menu"
            >
              <div className="absolute -inset-1.5 bg-blue-50 rounded-full blur opacity-0 group-hover:opacity-60 transition duration-300"></div>
              <div className="relative flex items-center justify-center transition-colors duration-300 border border-gray-200 rounded-lg w-9 h-9 group-hover:border-blue-300">
                {isNavOpen ? (
                  <FaTimes className="w-5 h-5 text-gray-700 group-hover:text-blue-700" />
                ) : (
                  <FaBars className="w-5 h-5 text-gray-700 group-hover:text-blue-700" />
                )}
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Animated Mobile Sidebar */}
      <AnimatePresence>
        {isNavOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-black/40 lg:hidden"
              onClick={() => setIsNavOpen(false)}
            />

            {/* Sidebar */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed top-0 right-0 z-50 h-full max-w-full overflow-y-auto bg-white shadow-2xl w-72 lg:hidden"
            >
              {/* Sidebar Header */}
              <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center space-x-3">
                    <img src={AppLogo} className="w-auto h-10" alt="logo" />
                    <div>
                      <h2 className="text-base font-bold text-gray-900">
                        {LOGO_LABEL}
                      </h2>
                      <p className="text-xs font-semibold text-blue-700">
                        {LOGO_SLOGAN}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsNavOpen(false)}
                    className="flex items-center justify-center w-8 h-8 transition-colors rounded-lg hover:bg-gray-100"
                    aria-label="Close menu"
                  >
                    <FaTimes className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
                <p className="pt-3 text-xs italic text-gray-600 border-t border-gray-200">
                  Excellence in Education Since {ESTABLISH_YEAR}
                </p>
              </div>

              {/* Sidebar Navigation */}
              <nav className="p-5">
                <h3 className="mb-3 text-xs font-semibold tracking-wider text-gray-500 uppercase">
                  Navigation
                </h3>
                <div className="space-y-1">
                  {navItems.map((item, index) => (
                    <motion.div
                      key={item.path}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.08 }}
                    >
                      <Link
                        to={item.path}
                        onClick={() => setIsNavOpen(false)}
                        className="group flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-blue-50 transition-all duration-200 border border-transparent hover:border-blue-100"
                      >
                        <div className="text-sm text-blue-600 transition-colors group-hover:text-blue-700">
                          {item.icon}
                        </div>
                        <span className="text-sm font-medium text-gray-700 transition-colors group-hover:text-blue-700">
                          {item.label}
                        </span>
                        <div className="ml-auto transition-opacity duration-300 opacity-0 group-hover:opacity-100">
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>

                {/* Sidebar CTA */}
                <div className="pt-5 mt-6 border-t border-gray-100">
                  <button className="relative w-full mb-3 group">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-green-500 rounded-lg blur opacity-0 group-hover:opacity-60 transition duration-300"></div>
                    <div className="relative bg-gradient-to-r from-blue-700 to-blue-600 text-white py-2.5 rounded-lg text-sm font-semibold hover:from-blue-800 hover:to-blue-700 transition-all duration-300">
                      Apply Now
                    </div>
                  </button>
                </div>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
